import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import {
  getAllOrders,
  getAllPaidOrders,
  getAllPaidOrdersDashboard,
  getAllUnpaidOrders,
} from "../actions/orderActions";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import Loader from "../components/Loader";
import Pagination from "../components/Pagination";
import NotAuthorized from "../components/NotAuthorized";
import exportFromJSON from "export-from-json";
import "../css/records.css";

// Legacy orders stored promoCode as the full promo document ({_id, name,
// code, ...}); newer orders store just the code string. Rendering the raw
// object as a JSX child crashes React ("Objects are not valid as a React
// child"), which blanks the whole page since there's no error boundary.
const promoCodeLabel = (promoCode) => {
  if (!promoCode) return "--";
  if (typeof promoCode === "string") return promoCode;
  return promoCode.code || promoCode.name || "--";
};

const OrdersScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [name, setName] = useState(searchParams.get("name") || "");
  const [from, setFrom] = useState(searchParams.get("from") || "");
  const [to, setTo] = useState(searchParams.get("to") || "");

  const [paymentStatus, setPaymentStatus] = useState(
    searchParams.get("tab") || "all"
  );

  const [pageAll, setPageAll] = useState(
    Number(searchParams.get("pageAll")) || 1
  );
  const [pagePaid, setPagePaid] = useState(
    Number(searchParams.get("pagePaid")) || 1
  );
  const [pageUnpaid, setPageUnpaid] = useState(
    Number(searchParams.get("pageUnpaid")) || 1
  );

  const [pageCountAll, setPageCountAll] = useState(0);
  const [pageCountPaid, setPageCountPaid] = useState(0);
  const [pageCountUnpaid, setPageCountUnpaid] = useState(0);

  useEffect(() => {
    setSearchParams(
      {
        tab: paymentStatus,
        pageAll,
        pagePaid,
        pageUnpaid,
        name,
        from,
        to,
      },
      { replace: true }
    );
  }, [paymentStatus, pageAll, pagePaid, pageUnpaid, name, from, to]);

  const allOrders = useSelector((state) => state.allOrders);
  const { orders: allOrdersList, pagination: allOrdersPagination } =
    allOrders || {};

  useEffect(() => {
    dispatch(getAllPaidOrdersDashboard());
  }, []);

  const fullPaidOrders = useSelector((state) => state.paidOrdersFull);
  const { order: fullPaidOrderList } = fullPaidOrders;

  const paidOrders = useSelector((state) => state.paidOrder);
  const { orders: paidOrdersList, pagination: paidOrdersPagination } =
    paidOrders || {};
  const unpaidOrders = useSelector((state) => state.unpaidOrder);
  const { orders: unpaidOrdersList, pagination: unpaidOrdersPagination } =
    unpaidOrders || {};

  useEffect(() => {
    setPageCountAll(allOrdersPagination?.pageCount || 0);
  }, [allOrdersPagination]);

  useEffect(() => {
    setPageCountPaid(paidOrdersPagination?.pageCount || 0);
  }, [paidOrdersPagination]);

  useEffect(() => {
    setPageCountUnpaid(unpaidOrdersPagination?.pageCount || 0);
  }, [unpaidOrdersPagination]);

  useEffect(() => {
    dispatch(getAllOrders(pageAll, name, from, to));
  }, [dispatch, pageAll]);

  useEffect(() => {
    dispatch(getAllPaidOrders(pagePaid, name, from, to));
  }, [dispatch, pagePaid]);

  useEffect(() => {
    dispatch(getAllUnpaidOrders(pageUnpaid, name, from, to));
  }, [dispatch, pageUnpaid]);
  const searchQuery = () => {
    setPageAll(1);
    setPagePaid(1);
    setPageUnpaid(1);

    dispatch(getAllOrders(1, name, from, to));
    dispatch(getAllPaidOrders(1, name, from, to));
    dispatch(getAllUnpaidOrders(1, name, from, to));
  };

  const [currentExportList, setCurrentExportList] = useState([]);
  const [currentExportLoading, setCurrentExportLoading] = useState(true);

  useEffect(() => {
    let list;
    list = fullPaidOrderList;
    // console.log(fullPaidOrderList.length);
    // If list is still undefined/null → assume backend still fetching
    if (list === undefined || list === null) {
      setCurrentExportLoading(true);
      setCurrentExportList([]);
    } else {
      // Once list is defined (even if []), we consider it "loaded"
      setCurrentExportList(Array.isArray(list) ? list : []);
      setCurrentExportLoading(false);
    }
  }, [paymentStatus, fullPaidOrderList]);

  const isExportDisabled =
    currentExportLoading ||
    !currentExportList ||
    currentExportList.length === 0;

  const exportCSV = () => {
    let orders = fullPaidOrderList;

    if (orders && orders.length) {
      let data = [];

      for (let i = 0; i < orders.length; i++) {
        const order = orders[i];
        const items = order.items || [];

        // ---- Build item[i] fields dynamically ----
        const itemFields = {};

        items.forEach((item, index) => {
          itemFields[`item${index}_name`] = item?.name || "";
          itemFields[`item${index}_qty`] = item?.qty ?? item?.quantity ?? "";
          itemFields[`item${index}_color`] =
            item?.colorName || item?.color || "";
          itemFields[`item${index}_price`] = item?.price ?? item?.amount ?? "";
        });

        // ---- PaymentDetails flattening (only non-empty fields) ----
        const paymentDetails = order.paymentDetails || {};
        const paymentDetailsExport = {};

        Object.entries(paymentDetails).forEach(([key, value]) => {
          const isEmptyObject =
            value &&
            typeof value === "object" &&
            Object.keys(value).length === 0;

          if (
            value !== null &&
            value !== undefined &&
            value !== "" &&
            !isEmptyObject
          ) {
            paymentDetailsExport[`payment_${key}`] = value;
          }
        });

        data.push({
          orderID: "EM" + String(order._id).slice(-6).toUpperCase(),
          name: order?.name,
          email: order.email,
          phoneNumber: order.phoneNumber,
          createdAt: order?.createdAt.slice(0, 10),
          address: `${order.address.addressLine1} ${order.address.addressLine2}`,
          city: order.address.city,
          state: order.address.state,
          pincode: order.address.pincode,
          promocode: promoCodeLabel(order.promoCode),
          amount: order.amount,
          paymentStatus: order.paymentStatus,
          gstin: order.gstin,
          orderStatus: order.orderStatus,

          // Spread non-empty payment details
          paymentMethod: order?.paymentMethod,
          razorpayOrderID: order?.paymentDetails?.razorpayOrderId,
          razorpayPaymentID: order?.paymentDetails?.razorpayPaymentId,
          payuID: order?.paymentDetails?.payuId,
          bankRefNum: order?.paymentDetails?.bankRefNum, // First item name (your original)
          snapmintId: order?.paymentDetails?.snapmintId,
          applicationId: order?.paymentDetails?.applicationId,
          emiTenure: order?.paymentDetails?.emiTenure,
          emiAmount: order?.paymentDetails?.emiAmount,
          downPayment: order?.paymentDetails?.downPayment,

          // Spread dynamic item fields
          ...itemFields,
        });
      }

      const fileName = "orders";
      const exportType = exportFromJSON.types.csv;
      exportFromJSON({ data, fileName, exportType });
    }
  };

    const adminInfo = useSelector((state) => state.adminInfo);
  
    const { adminData } = adminInfo;

    const [isAllowed, setIsAllowed] = useState(false);
  
useEffect(() => {
  if (!adminData) return;

  const accessLevel =
   adminData.accessLevel === "Master" ||(adminData.accessLevel === "Viewer")

    console.log("Access Level:", accessLevel);

  setIsAllowed(accessLevel);
}, [adminData]);

  return (
    <div className="container-fluid ps-0 ">
      <div className="row ">
        <div className="col-lg-2">
          <Sidebar active={2} />
        </div>
        <div className="col-lg-10 p-0">
          {!isAllowed ? (
            <NotAuthorized />
          ) : (
            <>
              <nav className="w-100 mx-auto pt-3 ps-3">
                <div
                  class="nav records-tabs"
                  id="nav-tab"
                  role="tablist"
                >
                  <button
                    class="nav-link active"
                    id="nav-home-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#nav-home"
                    type="button"
                    role="tab"
                    aria-controls="nav-home"
                    aria-selected="true"
                    onClick={() => setPaymentStatus("all")}
                  >
                    ALL
                  </button>
                  <button
                    class="nav-link"
                    id="nav-profile-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#nav-profile"
                    type="button"
                    role="tab"
                    aria-controls="nav-profile"
                    aria-selected="false"
                    onClick={() => setPaymentStatus("paid")}
                  >
                    Paid
                  </button>
                  <button
                    class="nav-link"
                    id="nav-contact-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#nav-contact"
                    type="button"
                    role="tab"
                    aria-controls="nav-contact"
                    aria-selected="false"
                    onClick={() => setPaymentStatus("unpaid")}
                  >
                    UnPaid
                  </button>
                </div>
              </nav>

              {paymentStatus === "all" && allOrdersList && (
                <div className="records-page">
                  <h4 className="records-heading">Orders</h4>
                  <div className="records-toolbar">
                    <div class="form-floating">
                      <input
                        type="text"
                        class="form-control"
                        id="all-name-filter"
                        autoComplete="off"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="name@example.com"
                      />
                      <label for="all-name-filter">
                        Name or Mobile Number
                      </label>
                    </div>
                    <div class="form-floating">
                      <input
                        type="date"
                        class="form-control"
                        id="all-from-filter"
                        autoComplete="off"
                        value={from}
                        onChange={(e) => setFrom(e.target.value)}
                        placeholder="name@example.com"
                      />
                      <label for="all-from-filter">From</label>
                    </div>
                    <div class="form-floating">
                      <input
                        type="date"
                        class="form-control"
                        id="all-to-filter"
                        autoComplete="off"
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        placeholder="name@example.com"
                      />
                      <label for="all-to-filter">To</label>
                    </div>
                    <button
                      onClick={searchQuery}
                      className="btn btn-primary text-light records-icon-btn"
                    >
                      <i class="fa-solid fa-magnifying-glass"></i>
                    </button>
                    <button
                      onClick={exportCSV}
                      disabled={isExportDisabled}
                      className={`btn ${
                        isExportDisabled ? "btn-secondary" : "btn-success"
                      } text-light records-icon-btn`}
                    >
                      {isExportDisabled ? (
                        <i className="fa-solid fa-spinner fa-spin"></i>
                      ) : (
                        <i className="fa-solid fa-download"></i>
                      )}
                    </button>
                  </div>

                  <div className="records-status-bar">
                    {paymentStatus === "all" && (
                      <Pagination
                        page={pageAll}
                        pageCount={pageCountAll}
                        onChange={setPageAll}
                      />
                    )}
                    <div className="stat-pills">
                      <span className="stat-pill total">
                        <span className="stat-dot"></span>Total{" "}
                        <span className="stat-value">
                          {allOrdersList && allOrdersList.length}
                        </span>
                      </span>
                      <span className="stat-pill paid">
                        <span className="stat-dot"></span>Paid{" "}
                        <span className="stat-value">
                          {allOrdersList &&
                            allOrdersList.filter(
                              (order) => order.paymentStatus === true,
                            ).length}
                        </span>
                      </span>
                      <span className="stat-pill unpaid">
                        <span className="stat-dot"></span>Unpaid{" "}
                        <span className="stat-value">
                          {allOrdersList &&
                            allOrdersList.filter(
                              (order) => order.paymentStatus === false,
                            ).length}
                        </span>
                      </span>
                    </div>
                  </div>

                  <div
                    className="table-card"
                    style={{
                      height: "70vh",
                      overflowY: "auto",
                    }}
                  >
                    <table className="records-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Mob. No.</th>
                          <th>Items</th>
                          <th>Promocode</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paymentStatus === "all" &&
                          allOrdersList &&
                          allOrdersList.map((order) => (
                            <tr
                              className={order.paymentStatus ? "row-paid" : ""}
                              style={{
                                cursor: "pointer",
                              }}
                              onClick={() => navigate(`/orders/${order._id}`)}
                            >
                              <td>{order.createdAt.slice(0, 10)}</td>
                              <td>{order.name}</td>
                              <td>{order.email}</td>
                              <td>{order.phoneNumber}</td>
                              <td
                                data-toggle="tooltip"
                                data-placement="top"
                                title={order.items.map((name) => name.name)}
                              >
                                {order.items.reduce(
                                  (acc, item) => acc + item.qty,
                                  0,
                                )}
                              </td>
                              <td>
                                {promoCodeLabel(order.promoCode)}
                              </td>
                              <td>{order.amount}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {paymentStatus === "paid" && paidOrdersList && (
                <div className="records-page">
                  <h4 className="records-heading">Orders</h4>
                  <div className="records-toolbar">
                    <div class="form-floating">
                      <input
                        type="text"
                        class="form-control"
                        id="paid-name-filter"
                        autoComplete="off"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="name@example.com"
                      />
                      <label for="paid-name-filter">
                        Name or Mobile Number
                      </label>
                    </div>
                    <div class="form-floating">
                      <input
                        type="date"
                        class="form-control"
                        id="paid-from-filter"
                        autoComplete="off"
                        value={from}
                        onChange={(e) => setFrom(e.target.value)}
                        placeholder="name@example.com"
                      />
                      <label for="paid-from-filter">From</label>
                    </div>
                    <div class="form-floating">
                      <input
                        type="date"
                        class="form-control"
                        id="paid-to-filter"
                        autoComplete="off"
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        placeholder="name@example.com"
                      />
                      <label for="paid-to-filter">To</label>
                    </div>
                    <button
                      onClick={searchQuery}
                      className="btn btn-primary text-light records-icon-btn"
                    >
                      <i class="fa-solid fa-magnifying-glass"></i>
                    </button>
                    <button
                      onClick={exportCSV}
                      className="btn btn-success text-light records-icon-btn"
                    >
                      <i class="fa-solid fa-download"></i>
                    </button>
                  </div>

                  <div className="records-status-bar">
                    {paymentStatus === "paid" && (
                      <Pagination
                        page={pagePaid}
                        pageCount={pageCountPaid}
                        onChange={setPagePaid}
                      />
                    )}
                    <div className="stat-pills">
                      <span className="stat-pill paid">
                        <span className="stat-dot"></span>Paid{" "}
                        <span className="stat-value">
                          {paidOrdersList && paidOrdersList.length}
                        </span>
                      </span>
                    </div>
                  </div>

                  <div
                    className="table-card"
                    style={{
                      height: "70vh",
                      overflowY: "auto",
                    }}
                  >
                    <table className="records-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Mob. No.</th>
                          <th>Items</th>
                          <th>Promocode</th>
                          <th>Total</th>
                          <th>OMS Marked</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paymentStatus === "paid" &&
                          paidOrdersList &&
                          paidOrdersList.map((order) => (
                            <tr
                              className={order.paymentStatus ? "row-paid" : ""}
                              style={{
                                cursor: "pointer",
                              }}
                              onClick={() => navigate(`/orders/${order._id}`)}
                            >
                              <td>{order.createdAt.slice(0, 10)}</td>
                              <td>{order.name}</td>
                              <td>{order.email}</td>
                              <td>{order.phoneNumber}</td>
                              <td
                                data-toggle="tooltip"
                                data-placement="top"
                                title={order.items.map((name) => name.name)}
                              >
                                {order.items.reduce(
                                  (acc, item) => acc + item.qty,
                                  0,
                                )}
                              </td>
                              <td>
                                {promoCodeLabel(order.promoCode)}
                              </td>
                              <td>{order.amount}</td>
                              <td>{order.omsMarked ? "TRUE" : ""}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {paymentStatus === "unpaid" && unpaidOrdersList && (
                <div className="records-page">
                  <h4 className="records-heading">Orders</h4>
                  <div className="records-toolbar">
                    <div class="form-floating">
                      <input
                        type="text"
                        class="form-control"
                        id="unpaid-name-filter"
                        autoComplete="off"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="name@example.com"
                      />
                      <label for="unpaid-name-filter">
                        Name or Mobile Number
                      </label>
                    </div>
                    <div class="form-floating">
                      <input
                        type="date"
                        class="form-control"
                        id="unpaid-from-filter"
                        autoComplete="off"
                        value={from}
                        onChange={(e) => setFrom(e.target.value)}
                        placeholder="name@example.com"
                      />
                      <label for="unpaid-from-filter">From</label>
                    </div>
                    <div class="form-floating">
                      <input
                        type="date"
                        class="form-control"
                        id="unpaid-to-filter"
                        autoComplete="off"
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        placeholder="name@example.com"
                      />
                      <label for="unpaid-to-filter">To</label>
                    </div>
                    <button
                      onClick={searchQuery}
                      className="btn btn-primary text-light records-icon-btn"
                    >
                      <i class="fa-solid fa-magnifying-glass"></i>
                    </button>
                    <button
                      onClick={exportCSV}
                      className="btn btn-success text-light records-icon-btn"
                    >
                      <i class="fa-solid fa-download"></i>
                    </button>
                  </div>

                  <div className="records-status-bar">
                    {paymentStatus === "unpaid" && (
                      <Pagination
                        page={pageUnpaid}
                        pageCount={pageCountUnpaid}
                        onChange={setPageUnpaid}
                      />
                    )}
                    <div className="stat-pills">
                      <span className="stat-pill unpaid">
                        <span className="stat-dot"></span>Unpaid{" "}
                        <span className="stat-value">
                          {unpaidOrdersList && unpaidOrdersList.length}
                        </span>
                      </span>
                    </div>
                  </div>

                  <div
                    className="table-card"
                    style={{
                      height: "70vh",
                      overflowY: "auto",
                    }}
                  >
                    <table className="records-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Mob. No.</th>
                          <th>Items</th>
                          <th>Promocode</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paymentStatus === "unpaid" &&
                          unpaidOrdersList &&
                          unpaidOrdersList.map((order) => (
                            <tr
                              className={order.paymentStatus ? "row-paid" : ""}
                              style={{
                                cursor: "pointer",
                              }}
                              onClick={() => navigate(`/orders/${order._id}`)}
                            >
                              <td>{order.createdAt.slice(0, 10)}</td>
                              <td>{order.name}</td>
                              <td>{order.email}</td>
                              <td>{order.phoneNumber}</td>
                              <td
                                data-toggle="tooltip"
                                data-placement="top"
                                title={order.items.map((name) => name.name)}
                              >
                                {order.items.reduce(
                                  (acc, item) => acc + item.qty,
                                  0,
                                )}
                              </td>
                              <td>{promoCodeLabel(order.promoCode)}</td>
                              <td>{order.amount}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {paymentStatus === "all" && !allOrdersList && <Loader />}
              {paymentStatus === "paid" && !paidOrdersList && <Loader />}
              {paymentStatus === "unpaid" && !unpaidOrdersList && <Loader />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrdersScreen;
